// app/index.tsx

import React, { useEffect, useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  TouchableWithoutFeedback,
  ImageBackground,
  Modal,
  Animated,
  Easing,
  Image,
  TextInput,
  Button,
  ActivityIndicator,
} from 'react-native';
import Slider from '@react-native-community/slider';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getBrightness, setBrightness } from '../services/api';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

const AREA_KEYS = [
  'lobiGenel',
  'lobiBar',
  'akaRestaurantGenel',
  'fitnessGenel',
  'ustLobiGenel',
  'steakHouseGenel',
  'chefRestaurantGenel',
  'kulRestaurantlGenel',
] as const;
type AreaKey = typeof AREA_KEYS[number];
const AREA_LABELS: Record<AreaKey, string> = {
  lobiGenel: 'Lobby Bölgesi Genel',
  lobiBar: 'Lobby Bar',
  akaRestaurantGenel: 'Aka Restaurant',
  fitnessGenel: 'Fitness Center',
  ustLobiGenel: 'Lobby Üst Kat Genel',
  steakHouseGenel: 'Fuego Steak Restaurant',
  chefRestaurantGenel: 'Chef Restaurant',
  kulRestaurantlGenel: 'Kül Restaurant',
};

export default function MetroScreen() {
  const insets = useSafeAreaInsets();

  // ---------- HOOKS ----------
  // Authentication hooks
  const [authLoading, setAuthLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  useEffect(() => {
    AsyncStorage.getItem('authenticated').then(saved => {
      if (saved === 'true') setAuthenticated(true);
      setAuthLoading(false);
    });
  }, []);

  // Brightness hooks (declared always)
  const [values, setValues] = useState<Record<AreaKey, number>>({} as any);
  const [activeKey, setActiveKey] = useState<AreaKey | null>(null);
  const [tempValue, setTempValue] = useState(0);
  const scaleAnim = useRef(new Animated.Value(0)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;

  // Polling effect runs after authentication
  useEffect(() => {
    if (!authenticated) return;
    let mounted = true;
    const loadData = async () => {
      const data = await getBrightness();
      if (!mounted) return;
      const parsed: any = {};
      Object.entries(data).forEach(([k, v]) => {
        parsed[k as AreaKey] = parseInt(v as string, 10);
      });
      setValues(parsed);
    };
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [authenticated]);

  // Early returns (only render differs, but hooks always called above)
  if (authLoading) {
    return <ActivityIndicator style={styles.centered} size="large" />;
  }
  if (!authenticated) {
    return (
      <View style={[styles.centered, { padding: 20 }]}> 
        <Text style={styles.loginText}>Uygulama Şifresi:</Text>
        <TextInput
          style={styles.passwordInput}
          secureTextEntry
          keyboardType="numeric"
          maxLength={4}
          placeholder="Şifreyi girin"
          value={passwordInput}
          onChangeText={setPasswordInput}
        />
        <Button
          title="Giriş Yap"
          onPress={async () => {
            if (passwordInput === '1997') {
              await AsyncStorage.setItem('authenticated', 'true');
              setAuthenticated(true);
            } else {
              alert('Yanlış şifre');
            }
          }}
        />
      </View>
    );
  }

  // ---------- UI FUNCTIONS ----------
  const openModal = (key: AreaKey) => {
    setTempValue(values[key] ?? 0);
    setActiveKey(key);
    scaleAnim.setValue(0.8);
    opacityAnim.setValue(0);
    Animated.parallel([
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 300,
        easing: Easing.out(Easing.poly(5)),
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnim, {
        toValue: 1,
        duration: 300,
        easing: Easing.out(Easing.poly(5)),
        useNativeDriver: true,
      }),
    ]).start();
  };
  const closeModal = () => {
    Animated.parallel([
      Animated.timing(scaleAnim, {
        toValue: 0.8,
        duration: 250,
        easing: Easing.out(Easing.poly(5)),
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: 250,
        easing: Easing.out(Easing.poly(5)),
        useNativeDriver: true,
      }),
    ]).start(() => setActiveKey(null));
  };

  const getAreaImage = (key: AreaKey) => {
    switch (key) {
      case 'lobiGenel': return require('../assets/images/lobiGenel.jpg');
      case 'lobiBar': return require('../assets/images/lobiBar.jpg');
      case 'akaRestaurantGenel': return require('../assets/images/akaRestaurantGenel.jpg');
      case 'fitnessGenel': return require('../assets/images/fitnessGenel.jpg');
      case 'ustLobiGenel': return require('../assets/images/ustLobiGenel.jpg');
      case 'steakHouseGenel': return require('../assets/images/steakHouseGenel.jpg');
      case 'chefRestaurantGenel': return require('../assets/images/chefRestaurantGenel.jpg');
      case 'kulRestaurantlGenel': return require('../assets/images/kulRestaurantlGenel.jpg');
      default: return null;
    }
  };

  // Grid layout
  const cols = 2, spacingVal = 8;
  const baseTile = (screenWidth - spacingVal * (cols + 1)) / cols;
  const tileSize = baseTile * 0.85;

  return (
    <ImageBackground
      source={require('../assets/images/lobby.jpg')}
      style={[styles.background, { paddingTop: insets.top + 8 }]}
      resizeMode="cover"
    >
      <View style={styles.grid}>
        {AREA_KEYS.map(key => (
          <TouchableWithoutFeedback key={key} onPress={() => openModal(key)}>
            <View style={[styles.tile, { width: tileSize, height: tileSize, margin: spacingVal / 2 }]}>  
              <Text style={styles.label}>{AREA_LABELS[key]}</Text>
              <Text style={styles.value}>{values[key] ?? 0}%</Text>
            </View>
          </TouchableWithoutFeedback>
        ))}
      </View>

      {/* Logo Pill */}
      <View style={styles.logoWrapper} pointerEvents="none">
        <View style={styles.logoPill}>
          <Image
            source={require('../assets/images/logo.png')}
            style={styles.logoPillImage}
            resizeMode="contain"
          />
        </View>
      </View>

      {/* Detail Modal */}
      <Modal transparent visible={activeKey !== null} animationType="none">
        <TouchableWithoutFeedback onPress={closeModal}>
          <View style={styles.overlay} />
        </TouchableWithoutFeedback>
        {activeKey && (
          <Animated.View style={[styles.modalContent, { transform: [{ scale: scaleAnim }], opacity: opacityAnim }]}> 
            <Image source={getAreaImage(activeKey)} style={styles.modalImage} resizeMode="cover" />
            <Text style={styles.modalTitle}>{AREA_LABELS[activeKey]}</Text>
            <Text style={styles.modalValue}>{tempValue}%</Text>
            <Slider
              style={styles.modalSlider}
              minimumValue={0}
              maximumValue={activeKey === 'lobiGenel' ? 50 : 100}
              step={1}
              value={tempValue}
              onValueChange={setTempValue}
              onSlidingComplete={async val => {
                await setBrightness(activeKey!, val);
                setValues(prev => ({ ...prev, [activeKey!]: val }));
              }}
              minimumTrackTintColor="#C0A15A"
              maximumTrackTintColor="#DDD"
              thumbTintColor="#C0A15A"
            />
          </Animated.View>
        )}
      </Modal>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loginText: { fontSize: 18, marginBottom: 12 },
  passwordInput: { width: '100%', borderColor: '#ccc', borderWidth: 1, borderRadius: 8, padding: 8, marginBottom: 12 },
  background: { flex: 1, width: screenWidth, height: screenHeight },
  grid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', padding: 8 },
  tile: { backgroundColor: 'rgba(50,50,50,0.5)', borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  label: { color: '#fff', fontSize: 14, fontWeight: '600', textAlign: 'center', paddingHorizontal: 4 },
  value: { color: '#f2f2f2', fontSize: 12, marginTop: 4 },
  logoWrapper: { position: 'absolute', bottom: 32, left: 0, right: 0, alignItems: 'center' },
  logoPill: { paddingHorizontal: 20, paddingVertical: 8, borderRadius: 30, backgroundColor: 'rgba(255,255,255,0.8)', shadowColor: '#000', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.25, shadowRadius: 5, elevation: 6, alignItems: 'center' },
  logoPillImage: { width: 160, height: 50, opacity: 1 },
  overlay: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.5)' },
  modalContent: { position: 'absolute', top: '10%', left: '5%', width: '90%', backgroundColor: '#fff', borderRadius: 12, padding: 16, alignItems: 'center' },
  modalImage: { width: '100%', height: 150, borderRadius: 12, marginBottom: 12 },
  modalTitle: { fontSize: 18, fontWeight: '700', color: '#224957', marginBottom: 8 },
  modalValue: { fontSize: 16, color: '#333', marginBottom: 12 },
  modalSlider: { width: '100%', height: 40 },
});
